import { Divider, IconButton, Modal, Stack, Typography } from "@mui/material";
import { desktopSx } from "src/utils/styles/modalStyles";
import { useInputs } from "./useInputs";
import Input from "src/components/FormInput";
import { Close } from "@mui/icons-material";
import LoadingButton from "src/components/Buttons/LoadingButton";
import { FormEvent, useCallback, useState } from "react";
import { useCreateDaysSupplyMutation } from "src/api/services/daysSupplyApi";
import { ProductPrice } from "src/api/types";
import { useSentryLogger } from "src/hooks/useSentryLogger";

interface Props {
  open: boolean;
  onClose: () => void;
  price: ProductPrice;
}

const AddDaysSupplyModal = ({ onClose, open, price }: Props) => {
  const { inputs, values, setValues } = useInputs();
  const [loading, setLoading] = useState<boolean>(false);
  const [createDaysSupply] = useCreateDaysSupplyMutation();
  const errLog = useSentryLogger("error");

  const handleSubmit = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      setLoading(true);

      try {
        await createDaysSupply({
          days_supply: values.days_supply,
          price_id: price.id,
          product_id: price.product_id,
          product_category_id: price.product_category_id,
        }).unwrap();

        setValues({ days_supply: 0 });
        localStorage.removeItem("open-days-supply");
        onClose();
      } catch (err) {
        errLog(`Unable to create days supply for price: ${price.id}`, err);
      } finally {
        setLoading(false);
      }
    },
    [
      createDaysSupply,
      errLog,
      onClose,
      price.id,
      price.product_category_id,
      price.product_id,
      setValues,
      values.days_supply,
    ],
  );

  const handleClose = useCallback(() => {
    localStorage.removeItem("open-days-supply");
    onClose();
  }, [onClose]);

  return (
    <Modal open={open} onClose={handleClose}>
      <Stack sx={desktopSx}>
        <Stack gap={3}>
          <Stack gap={1}>
            <Stack
              direction={"row"}
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="h5" component="h5" fontWeight={700}>
                Add days supply
              </Typography>
              <IconButton onClick={handleClose} sx={{ p: 0 }}>
                <Close fontSize="medium" />
              </IconButton>
            </Stack>
            <Divider sx={{ backgroundColor: "#000000" }} />
          </Stack>
          <Stack component={"form"} onSubmit={handleSubmit} gap={2}>
            {inputs.map((input) => (
              <Input key={input.label} current={input} />
            ))}
            <LoadingButton loading={loading} type="submit">
              Create
            </LoadingButton>
          </Stack>
        </Stack>
      </Stack>
    </Modal>
  );
};

export default AddDaysSupplyModal;
